import React, { FC, useEffect, useState } from "react";
import { styled } from "@material-ui/styles";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";

import ConfirmationModal from "../../../modules/ConfirmationModal/ConfirmationModal";
import api from "../../../../api";
import hubspotLogo from "../../../../assets/icons/HubspotLogo.svg";
import { AdvertiserType } from "../../../../types/advertiserTypes";
import { ReduxApplicationState } from "../../../../types/ReduxTypes/ReduxApplicationState";
import { setAgencyAdvertisers } from "../../../../redux/reducers/agency";

type Props = {
  advertiser: AdvertiserType;
  fetchAndSetAdvertiserDetailData: () => void;
};

const Card = styled("div")({
  background: "#FFFFFF",
  border: "1px solid #E5E7EB",
  borderRadius: 10,
  padding: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 16,
  flexWrap: "wrap",
});

const Left = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 16,
});

const Logo = styled("img")({
  width: 40,
  height: 40,
  flexShrink: 0,
});

const StatusText = styled("div")({
  fontSize: 13,
  fontWeight: 600,
  color: "#10B981",
});

const NotConnectedText = styled("div")({
  fontSize: 13,
  fontWeight: 600,
  color: "#6B7280",
});

const Label = styled("div")({
  fontSize: 13,
  color: "#6B7280",
  marginTop: 2,
});

const Buttons = styled("div")({
  display: "flex",
  gap: 8,
});

const OutlineBtn = styled("button")({
  background: "#FFFFFF",
  border: "1px solid #D1D5DB",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  color: "#374151",
  padding: "8px 14px",
  cursor: "pointer",
  "&:hover": { background: "#F9FAFB" },
  "&:disabled": { opacity: 0.5, cursor: "not-allowed" },
});

const fetchHubspotAuthUrl = async (bgAdvertiserId: string): Promise<string> => {
  const { payload } = await api.getHubspotAuth(bgAdvertiserId);
  return payload;
};

const HubSpotCard: FC<Props> = ({ advertiser, fetchAndSetAdvertiserDetailData }) => {
  const { hubspot_authed, hubspot_portal_id, brandgenAdvertiserId, name } = advertiser;

  const dispatch = useDispatch();
  const agencyId = useSelector((state: ReduxApplicationState) => state.agency.agencyId);
  const location = useLocation();
  const history = useHistory();

  const [hubspotAuthUrl, setHubspotAuthUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);

    const loadAuthUrl = async () => {
      try {
        const url = await fetchHubspotAuthUrl(brandgenAdvertiserId);
        setHubspotAuthUrl(url);
      } catch (error) {
        console.log("error fetchHubspotAuthUrl:", error);
      }
    };

    const handleOAuthCallback = async () => {
      try {
        const code = window.location.href.split("?").slice(-1)[0].split("=").slice(-1)[0].trim();
        setIsLoading(true);
        await api.createHubspotTokens(code, brandgenAdvertiserId);
        await fetchAndSetAdvertiserDetailData();
        dispatch(setAgencyAdvertisers(agencyId));
        queryParams.delete("code");
        history.replace({ search: queryParams.toString() });
      } catch (error) {
        console.log("error handleOAuthCallback:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (queryParams.has("code")) {
      handleOAuthCallback();
    }

    loadAuthUrl();
  }, [agencyId, brandgenAdvertiserId, dispatch, fetchAndSetAdvertiserDetailData, history, location.search]);

  const handleConnect = () => {
    localStorage.setItem("currentBrandgenAdvertiserId", brandgenAdvertiserId);
    window.location.href = hubspotAuthUrl;
  };

  const handleDisconnect = async () => {
    try {
      setIsLoading(true);
      await api.deleteHubspotTokens(brandgenAdvertiserId);
      await fetchAndSetAdvertiserDetailData();
      dispatch(setAgencyAdvertisers(agencyId));
    } catch (error) {
      console.log("error handleDisconnect:", error);
    } finally {
      setIsLoading(false);
      setIsConfirmModalOpen(false);
    }
  };

  return (
    <>
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        handleClose={() => setIsConfirmModalOpen(false)}
        handleConfirm={handleDisconnect}
        confirmButtonLabel="Disconnect"
        confirmQuestion={`Disconnect "${name}" Advertiser from HubSpot`}
      />

      <Card>
        <Left>
          <Logo src={hubspotLogo} alt="HubSpot" />
          <div>
            {hubspot_authed ? (
              <StatusText>Account ID: {hubspot_portal_id}</StatusText>
            ) : (
              <NotConnectedText>Not Connected</NotConnectedText>
            )}
            <Label>HubSpot Account</Label>
          </div>
        </Left>

        <Buttons>
          {hubspot_authed && (
            <OutlineBtn onClick={() => setIsConfirmModalOpen(true)} disabled={isLoading}>
              Disconnect
            </OutlineBtn>
          )}
          <OutlineBtn onClick={handleConnect} disabled={isLoading || !hubspotAuthUrl}>
            {hubspot_authed ? "Update" : "Connect"}
          </OutlineBtn>
        </Buttons>
      </Card>
    </>
  );
};

export default HubSpotCard;
